import { FileTextIcon, PlusIcon, ReceiptTextIcon, SearchIcon } from "lucide-react"
import type * as React from "react"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import { DocumentStatusBadge, useStatusLabel } from "@/features/invoices/DocumentStatusBadge"
import {
  ActiveQuotationFilterChips,
  QuotationFilterSheet,
} from "@/features/quotations/QuotationFilterSheet"
import { DraftsStrip } from "@/features/drafts/DraftsStrip"
import {
  QUOTATION_PAGE_SIZE,
  useQuotationFilters,
  useQuotationList,
  type QuotationSortKey,
} from "@/features/quotations/use-quotation-list"
import { SyncStatus } from "@/components/SyncStatus"
import { SortableHead } from "@/components/SortableHead"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useAuth } from "@/features/auth/auth-context"
import { ApiError } from "@/lib/api-error"
import { useFormatDay } from "@/lib/dates"
import type { QuotationListRow } from "@/lib/endpoints"
import { formatRupees } from "@/lib/money"
import { routes } from "@/routes/paths"

/**
 * The quotation register (§7, §9.3, §4).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE AMOUNT SHOWN IS `display.printedGrandTotalPaise`, NEVER `grandTotalPaise`.
 *
 * A TAX_EXTRA quotation quotes a PRE-TAX figure — the existing Excel documents
 * print "SGST 9% EXTRA" against the tax lines, so the number the customer was
 * told is the subtotal, and that is what appears under "G. Total" on their copy.
 * The server resolves which of the two totals that is; this screen renders the
 * answer.
 *
 * That distinction has been got wrong FIVE times in this codebase, every time by
 * a screen deciding for itself. Showing `grandTotalPaise` on a TAX_EXTRA
 * quotation puts a figure on screen that appears nowhere on the customer's copy,
 * and the owner then quotes it down the phone.
 *
 * VOID QUOTATIONS ARE HIDDEN BY DEFAULT — the server does that (§4). Note this
 * is the OPPOSITE of the invoice register, where cancelled bills stay visible
 * and marked (§17.4): a cancelled invoice holds a consumed number in a gap-free
 * series and must be seen; a withdrawn offer need not be.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function QuotationListScreen(): React.JSX.Element {
  const { t } = useTranslation()
  const { can } = useAuth()
  const formatDay = useFormatDay()
  const statusLabel = useStatusLabel()

  const { filters, setFilters, clearFilters, activeCount } = useQuotationFilters()
  const query = useQuotationList(filters)

  const sortBy = (sort: QuotationSortKey | undefined, dir: "asc" | "desc" | undefined) => {
    setFilters({ sort, dir })
  }

  const rows = query.data?.data ?? []
  const total = query.data?.total ?? 0
  const first = total === 0 ? 0 : filters.offset + 1
  const last = Math.min(filters.offset + rows.length, total)

  return (
    <div className="flex flex-col gap-4 p-4 md:gap-6 md:p-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t("nav.quotations")}</h1>
          <p className="text-muted-foreground max-w-2xl text-sm">{t("quotation.listSubtitle")}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* The register is the screen most likely to be stale — a bill his
            * son raised on the shop computer will not appear here until
            * something refetches. */}
          <SyncStatus
            updatedAt={query.dataUpdatedAt}
            syncing={query.isFetching}
            onSync={() => {
              void query.refetch()
            }}
          />
          {can("quotation.create") ? (
          <Button nativeButton={false} render={<Link to={routes.quotationNew} />}>
            <PlusIcon />
            {t("btn.newQuotation")}
          </Button>
        ) : null}
        </div>
      </header>

      {/*
        * ABOVE THE REGISTER, AND ABOVE THE FILTERS. An unfinished offer is a
        * to-do, not a record — it has no number, so no filter here can reach it
        * and no sort can order it among documents that do. This is the screen
        * the owner opens looking for "the quotation I was writing yesterday",
        * and before this existed a saved quotation draft appeared nowhere he
        * would think to look.
        *
        * Gated on `quotation.create`, matching the endpoint: reading the
        * register does not entitle you to somebody's half-typed form.
        */}
      {can("quotation.create") ? <DraftsStrip kind="QUOTATION" /> : null}

      <div className="flex flex-col gap-3">
        <QuotationFilterBar
          filters={filters}
          setFilters={setFilters}
          clearFilters={clearFilters}
          activeCount={activeCount}
        />
        {/* What is currently narrowing the list, removable one at a time. */}
        <ActiveQuotationFilterChips filters={filters} setFilters={setFilters} />
      </div>

      {query.isError ? (
        <Alert variant="destructive">
          <AlertTitle>{t("err.couldNotLoad")}</AlertTitle>
          <AlertDescription className="flex flex-col items-start gap-3">
            <span>
              {query.error instanceof ApiError ? query.error.message : t("err.somethingWentWrong")}
            </span>
            <Button
              variant="outline"
              onClick={() => {
                void query.refetch()
              }}
            >
              {t("btn.retry")}
            </Button>
          </AlertDescription>
        </Alert>
      ) : query.isPending ? (
        <ListSkeleton />
      ) : rows.length === 0 ? (
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <FileTextIcon />
            </EmptyMedia>
            <EmptyTitle>
              {activeCount > 0 ? t("list.noneMatch") : t("quotation.emptyTitle")}
            </EmptyTitle>
            <EmptyDescription>
              {activeCount > 0 ? t("list.noneMatchHelp") : t("quotation.emptyHelp")}
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            {activeCount > 0 ? (
              <Button variant="outline" onClick={clearFilters}>
                {t("list.clearFilters")}
              </Button>
            ) : can("quotation.create") ? (
              <Button nativeButton={false} render={<Link to={routes.quotationNew} />}>
                <PlusIcon />
                {t("btn.newQuotation")}
              </Button>
            ) : null}
          </EmptyContent>
        </Empty>
      ) : (
        <>
          {/* Phone: one card per quotation. */}
          <Card className="overflow-hidden py-0 md:hidden">
            <ul className="flex flex-col">
              {rows.map((row) => (
                <li
                  key={row.id}
                  /*
                   * `relative` — the stretched link below resolves against this
                   * card, exactly as the desktop row does.
                   *
                   * THE CARD IS NOT ITSELF A LINK, AND THAT IS THE POINT. It was
                   * one until 2026-08-17: the whole <li> wrapped in a single
                   * <Link>, which put ConvertedMark's "view bill" anchor INSIDE
                   * the card's anchor. Nested <a> is invalid HTML and React said
                   * so on every render — but nothing failed, because the tests
                   * assert the two links EXIST, and they do. The breakage is at
                   * parse time: the browser unnests them, so which document the
                   * owner opened depended on where his thumb landed. He asks for
                   * the bill on a converted quotation and gets the quotation
                   * back, on the phone, which is where he actually works.
                   */
                  className="hover:bg-accent/50 relative border-b transition-colors last:border-b-0"
                >
                  <div className="flex min-h-14 items-center gap-3 p-4">
                    <span className="flex min-w-0 flex-1 flex-col gap-1">
                      <span className="flex flex-wrap items-center gap-2">
                        <Link
                          to={routes.quotation(row.id)}
                          aria-label={t("list.rowLabel", {
                            number: row.number,
                            customer:
                              row.customerName === "" ? t("lbl.customerUnknown") : row.customerName,
                            date: formatDay(row.date),
                            status: statusLabel(row.status, "quotation"),
                          })}
                          className="font-medium tabular-nums after:absolute after:inset-0"
                        >
                          {row.number}
                        </Link>
                        <DocumentStatusBadge status={row.status} kind="quotation" className="text-xs" />
                        <ConvertedMark row={row} />
                      </span>
                      <span className="truncate text-sm text-muted-foreground">
                        {row.customerName === "" ? t("lbl.customerUnknown") : row.customerName}
                      </span>
                      <span className="text-muted-foreground text-xs tabular-nums">
                        {formatDay(row.date)}
                      </span>
                    </span>
                    <QuotedAmount row={row} />
                  </div>
                </li>
              ))}
            </ul>
          </Card>

          {/* Desktop. */}
          <Card className="hidden overflow-hidden py-0 md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableHead column="number" label={t("lbl.number")} sort={filters.sort} dir={filters.dir} onSort={sortBy} />
                  <SortableHead column="date" label={t("lbl.date")} sort={filters.sort} dir={filters.dir} onSort={sortBy} />
                  <SortableHead column="customer" label={t("lbl.customer")} sort={filters.sort} dir={filters.dir} onSort={sortBy} />
                  <SortableHead column="subject" label={t("lbl.subject")} sort={filters.sort} dir={filters.dir} onSort={sortBy} />
                  <SortableHead column="status" label={t("lbl.status")} sort={filters.sort} dir={filters.dir} onSort={sortBy} />
                  <SortableHead column="total" align="end" label={t("lbl.total")} sort={filters.sort} dir={filters.dir} onSort={sortBy} />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  // `relative` — the stretched link resolves against the ROW.
                  <TableRow key={row.id} className="hover:bg-accent/50 relative cursor-pointer">
                    <TableCell className="font-medium tabular-nums">
                      <Link
                        to={routes.quotation(row.id)}
                        className="after:absolute after:inset-0 hover:underline"
                      >
                        {row.number}
                      </Link>
                    </TableCell>
                    <TableCell className="tabular-nums whitespace-nowrap">
                      {formatDay(row.date)}
                    </TableCell>
                    <TableCell className="max-w-[22ch] truncate">
                      {row.customerName === "" ? (
                        <span className="text-muted-foreground">{t("lbl.customerUnknown")}</span>
                      ) : (
                        row.customerName
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground max-w-[24ch] truncate text-sm">
                      {row.subject ?? "—"}
                    </TableCell>
                    <TableCell>
                      <span className="flex flex-wrap items-center gap-1.5">
                        <DocumentStatusBadge status={row.status} kind="quotation" />
                        <ConvertedMark row={row} />
                      </span>
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <QuotedAmount row={row} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          <Pager
            first={first}
            last={last}
            total={total}
            offset={filters.offset}
            onOffset={(offset) => {
              setFilters({ offset })
              window.scrollTo({ top: 0, behavior: "smooth" })
            }}
          />
        </>
      )}
    </div>
  )
}

/**
 * The figure the CUSTOMER was quoted (§9.3).
 *
 * `printedGrandTotalPaise` is pre-resolved by the server: the subtotal for a
 * TAX_EXTRA quotation, the true grand total otherwise. The "+ tax extra" note
 * beside it is what stops the number being mistaken for a tax-inclusive one —
 * without it, a pre-tax figure and a post-tax figure look identical on screen.
 */
function QuotedAmount({ row }: { row: QuotationListRow }): React.JSX.Element {
  const { t } = useTranslation()

  return (
    <span className="flex shrink-0 flex-col items-end">
      <span className="font-medium tabular-nums">
        {formatRupees(row.display.printedGrandTotalPaise)}
      </span>
      {row.display.taxIsExtra ? (
        <span className="text-muted-foreground text-xs">{t("quotation.taxExtra")}</span>
      ) : null}
    </span>
  )
}

/** §7.1 — a bill has been raised from this one. */
function ConvertedMark({ row }: { row: QuotationListRow }): React.JSX.Element | null {
  const { t } = useTranslation()
  if (row.convertedInvoiceId === null) return null

  return (
    <Link
      to={routes.invoice(row.convertedInvoiceId)}
      /*
       * `relative z-10` so this sits ABOVE the row's stretched link — without it
       * the row-wide overlay swallows the click and the owner lands on the
       * quotation instead of the bill he asked for.
       */
      className="text-muted-foreground hover:text-foreground relative z-10 inline-flex items-center gap-1 text-xs hover:underline"
    >
      <ReceiptTextIcon aria-hidden="true" className="size-3" />
      {t("btn.viewBill")}
    </Link>
  )
}

/**
 * The filters the API actually supports — and no others.
 *
 * THE SAME SHAPE AS THE BILL REGISTER'S TOOLBAR (§11.1): a number search, the
 * status, and everything else behind one "More filters" button. The two
 * registers sit next to each other in the sidebar and the owner moves between
 * them all day; a filter that lives in a different place on each is a filter he
 * has to hunt for twice.
 *
 * THE STATUS IS NO LONGER A NATIVE `<select>` (2026-08-16, at the owner's
 * request, for visual consistency with the rest of the UI). The reason the
 * native one was chosen is kept rather than deleted: on a phone it opened the
 * operating system's own full-height wheel — bigger than anything drawn in the
 * page, already familiar, and reliably hittable without the page having to draw
 * a 48px target. That target is now drawn by hand, as `` on every
 * `SelectItem` and on the trigger; the rem class rather than a pixel literal so
 * it grows with the large-text preference. DO NOT TIDY THAT CLASS AWAY — it is
 * the only thing keeping this control usable in a workshop. The full version of
 * this note is above `InvoiceFilterBar` in the bill register.
 *
 * THE DATES MOVED INTO THE SHEET. Two `DateField` buttons out here wrapped the
 * toolbar onto three rows on a phone, which pushed the register itself below
 * the fold on the screen whose entire job is to show it.
 */
function QuotationFilterBar({
  filters,
  setFilters,
  clearFilters,
  activeCount,
}: {
  filters: ReturnType<typeof useQuotationFilters>["filters"]
  setFilters: ReturnType<typeof useQuotationFilters>["setFilters"]
  clearFilters: () => void
  activeCount: number
}): React.JSX.Element {
  const { t } = useTranslation()

  /*
   * The eight QuotationStatus values plus "no filter". The list is declared once
   * and handed BOTH to `<Select items={…}>` and to the items themselves: Base UI
   * resolves the trigger's text from that array, and without it the trigger
   * prints the raw value — `PARTIALLY_CONVERTED` on screen, in English, on a
   * Marathi phone.
   */
  const statusOptions = [
    /* Empty sends no status, which the SERVER reads as "everything but VOID"
     * (§4). Choosing Cancelled is the only way to see those. */
    { value: "", label: t("list.anyStatus") },
    { value: "DRAFT", label: t("status.draft") },
    { value: "SENT", label: t("status.sent") },
    { value: "APPROVED", label: t("status.approved") },
    { value: "REJECTED", label: t("status.rejected") },
    { value: "PARTIALLY_CONVERTED", label: t("status.partiallyConverted") },
    { value: "CONVERTED", label: t("status.converted") },
    { value: "EXPIRED", label: t("status.expired") },
    { value: "VOID", label: t("status.cancelled") },
  ]

  return (
    <div className="flex flex-wrap items-end gap-3">
      {/*
        * SEARCH OVER THE QUOTATION'S OWN NUMBERS — `quote_number` and the
        * customer's P.O. number. It is a SERVER search (`?q=`), so it covers the
        * whole book rather than the fifty rows on this page, and it is what a
        * scanned Code 128 barcode off a printed quotation resolves through (§9).
        *
        * It does not match the customer name: that lives in a text-backed JSON
        * column the server will not query inside (CLAUDE.md). Finding a
        * customer's quotations is the customer filter, which is exact.
        */}
      <QuotationSearchBox filters={filters} setFilters={setFilters} />

      <div className="flex min-w-44 flex-col gap-1.5">
        <Label htmlFor="quotation-status">{t("list.statusFilter")}</Label>
        <Select
          items={statusOptions}
          value={filters.status ?? ""}
          onValueChange={(value) => {
            /*
             * "" MEANS NO FILTER, and it must leave as `undefined` rather than as
             * an empty parameter: the list schema is `.strict()`, so `?status=`
             * is a 400 rather than a no-op, and no status at all is what makes
             * the server hide VOID. Base UI hands the value over directly —
             * there is no `event.target` — and types it nullable, so null folds
             * into the same branch.
             */
            setFilters({ status: value === "" || value == null ? undefined : String(value) })
          }}
        >
          {/* The id goes on the TRIGGER, which is the button `<Label htmlFor>`
            * above labels — a <button> is a labelable element, so
            * `getByLabelText("Status")` still resolves this control. */}
          <SelectTrigger id="quotation-status" className="w-full">
            {/* The same string as the "" item: Base UI reads an empty value as
              * NO value, so without this the trigger would show a greyed-out
              * placeholder where the native select showed "All". */}
            <SelectValue placeholder={t("list.anyStatus")} />
          </SelectTrigger>
          {/* `w-auto` because the popup otherwise matches the trigger exactly and
            * clips "Partly turned into bills"; the OS wheel sized itself. */}
          <SelectContent className="w-auto min-w-(--anchor-width) max-w-[min(90vw,28rem)]">
            {statusOptions.map((option) => (
              /* `data-value` carries the MACHINE value onto the option element.
               * Base UI puts no value attribute on it, and a test that could only
               * read the translated label would stop proving that the enum the
               * `.strict()` API accepts is the one this control sends. */
              <SelectItem
                key={option.value}
                value={option.value}
                data-value={option.value}
                className="pl-3"
              >
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/*
        * EVERYTHING ELSE LIVES BEHIND ONE BUTTON. Status stays out here because
        * it is the filter reached for most — §4 hides VOID unless it is asked
        * for, and this select is the only way to ask.
        */}
      <QuotationFilterSheet
        filters={filters}
        setFilters={setFilters}
        clearFilters={clearFilters}
        activeCount={activeCount}
      />
    </div>
  )
}

/**
 * The number search.
 *
 * DEBOUNCED, and mirrored in local state rather than read straight from the URL.
 * Writing every keystroke to the URL fires a request per character and re-renders
 * the whole screen through the router, which on a phone makes the field feel
 * like it is dropping letters.
 */
function QuotationSearchBox({
  filters,
  setFilters,
}: {
  filters: ReturnType<typeof useQuotationFilters>["filters"]
  setFilters: ReturnType<typeof useQuotationFilters>["setFilters"]
}): React.JSX.Element {
  const { t } = useTranslation()
  const [text, setText] = useState(filters.q ?? "")

  // Follow the URL when it changes elsewhere — back button, "clear filters".
  useEffect(() => {
    setText(filters.q ?? "")
  }, [filters.q])

  useEffect(() => {
    const current = filters.q ?? ""
    if (text === current) return
    const timer = window.setTimeout(() => {
      setFilters({ q: text.trim() === "" ? undefined : text.trim() })
    }, 300)
    return () => {
      window.clearTimeout(timer)
    }
  }, [text, filters.q, setFilters])

  return (
    <div className="flex min-w-56 flex-1 flex-col gap-1.5 sm:max-w-xs">
      <Label htmlFor="quotation-search">{t("list.searchQuotations")}</Label>
      <div className="relative">
        <SearchIcon
          aria-hidden="true"
          className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2"
        />
        <Input
          id="quotation-search"
          type="search"
          className="pl-9 tabular-nums"
          placeholder={t("list.searchQuotationsPlaceholder")}
          value={text}
          onChange={(event) => {
            setText(event.target.value)
          }}
        />
      </div>
    </div>
  )
}

function Pager({
  first,
  last,
  total,
  offset,
  onOffset,
}: {
  first: number
  last: number
  total: number
  offset: number
  onOffset: (offset: number) => void
}): React.JSX.Element | null {
  const { t } = useTranslation()
  if (total <= QUOTATION_PAGE_SIZE) return null

  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <p className="text-muted-foreground text-sm tabular-nums">
        {t("list.showingRange", { first, last, total })}
      </p>
      <div className="flex gap-2">
        <Button
          variant="outline"
          disabled={offset === 0}
          onClick={() => {
            onOffset(Math.max(0, offset - QUOTATION_PAGE_SIZE))
          }}
        >
          {t("btn.previous")}
        </Button>
        <Button
          variant="outline"
          disabled={last >= total}
          onClick={() => {
            onOffset(offset + QUOTATION_PAGE_SIZE)
          }}
        >
          {t("btn.next")}
        </Button>
      </div>
    </div>
  )
}

function ListSkeleton(): React.JSX.Element {
  return (
    <Card className="overflow-hidden py-0">
      <ul className="flex flex-col">
        {[0, 1, 2, 3, 4].map((index) => (
          <li key={index} className="flex items-center gap-3 border-b p-4 last:border-b-0">
            <div className="flex flex-1 flex-col gap-2">
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-3 w-44" />
            </div>
            <Skeleton className="h-4 w-24" />
          </li>
        ))}
      </ul>
    </Card>
  )
}
